import React, { useState, useEffect } from 'react';
import { getProducts, updateProduct, createProduct, deleteProduct } from '../../services/api';
import GamifiedButton from '../common/GamifiedButton';

const StockManager = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
    const [currentProduct, setCurrentProduct] = useState({
        id: '',
        name: '',
        description: '',
        longDescription: '',
        price: '',
        originalPrice: '',
        weight: '',
        stockQuantity: '',
        imageUrl: '',
        badge: '',
        tagline: '',
        color: 'pink',
        variantType: 'standard',
        features: []
    });

    // New state for auto-calculation
    const [priceCalculation, setPriceCalculation] = useState({
        basePriceInput: '',
        discountPercent: ''
    });

    // Track unsaved changes
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        try {
            const data = await getProducts();
            setProducts(data);
        } catch {
            setError('Erreur lors du chargement des produits');
        } finally {
            setLoading(false);
        }
    };

    const openCreateModal = () => {
        setModalMode('create');
        setCurrentProduct({
            name: '',
            description: '',
            longDescription: '',
            price: '',
            originalPrice: '',
            weight: '',
            stockQuantity: '',
            imageUrl: '',
            badge: '',
            tagline: '',
            color: 'pink',
            variantType: 'standard',
            features: []
        });
        setPriceCalculation({ basePriceInput: '', discountPercent: '' });
        setHasUnsavedChanges(false);
        setValidationErrors({});
        setShowModal(true);
    };

    const openEditModal = (product) => {
        setModalMode('edit');

        // Calculate initial values for inputs
        const original = product.originalPrice ? parseFloat(product.originalPrice) : parseFloat(product.price);
        const current = parseFloat(product.price);
        let percent = '';

        if (product.originalPrice && original > current) {
            percent = Math.round(((original - current) / original) * 100);
        }

        setCurrentProduct({
            ...product,
            name: product.name || '',
            description: product.description || '',
            longDescription: product.longDescription || '',
            price: product.price || '',
            originalPrice: product.originalPrice || '',
            weight: product.weight || '',
            stockQuantity: product.stockQuantity || '',
            imageUrl: product.imageUrl || '',
            badge: product.badge || '',
            tagline: product.tagline || '',
            color: product.color || 'pink',
            variantType: product.variantType || 'standard',
            features: Array.isArray(product.features) ? product.features : []
        });

        setPriceCalculation({
            basePriceInput: original.toString(),
            discountPercent: percent.toString()
        });

        setHasUnsavedChanges(false);
        setValidationErrors({});

        setShowModal(true);
    };

    const handleModalChange = (e) => {
        const { name, value } = e.target;
        setHasUnsavedChanges(true);

        if (name === 'basePriceInput' || name === 'discountPercent') {
            setPriceCalculation(prev => ({
                ...prev,
                [name]: value
            }));
            // We don't update currentProduct here, it's calculated on render or submit
        } else {
            setCurrentProduct(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Handle modal close with unsaved changes confirmation
    const handleCloseModal = () => {
        if (hasUnsavedChanges) {
            const confirmClose = window.confirm(
                'Vous avez des modifications non sauvegardées. Voulez-vous vraiment quitter sans sauvegarder ?'
            );
            if (!confirmClose) return;
        }
        setShowModal(false);
        setHasUnsavedChanges(false);
        setValidationErrors({});
    };

    // Validate form before submit
    const validateForm = () => {
        const errors = {};

        if (!currentProduct.name?.trim()) {
            errors.name = 'Le nom du produit est obligatoire';
        }
        if (!priceCalculation.basePriceInput || parseFloat(priceCalculation.basePriceInput) <= 0) {
            errors.price = 'Le prix doit être supérieur à 0';
        }
        if (!currentProduct.stockQuantity && currentProduct.stockQuantity !== 0) {
            errors.stockQuantity = 'Le stock est obligatoire';
        }
        if (!currentProduct.imageUrl?.trim()) {
            errors.imageUrl = 'L\'URL de l\'image est obligatoire';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleModalSubmit = async (e) => {
        e.preventDefault();

        // Frontend validation
        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);

            // Calculate final values
            const base = parseFloat(priceCalculation.basePriceInput);
            const percent = parseFloat(priceCalculation.discountPercent) || 0;

            const badgeActuel = (currentProduct.badge || '').trim();
            // Un badge « -26% » est calculé, on peut le refaire ou l'effacer.
            // Un badge éditorial (« 🎉 BEST PRICE! ») a été écrit à la main :
            // l'écraser à chaque enregistrement le faisait disparaître sans
            // prévenir.
            const badgeCalcule = /^-\d+(\.\d+)?%$/.test(badgeActuel);

            let finalPrice = base;
            let finalOriginalPrice = null;
            let finalBadge = badgeCalcule ? '' : badgeActuel;

            if (percent > 0) {
                finalPrice = base * (1 - percent / 100);
                finalOriginalPrice = base;
                finalBadge = `-${percent}%`;
            }

            // Puces nettoyées : on retire les lignes vides, on ne remplace
            // JAMAIS une liste vide par un texte par défaut (l'ancien
            // ['Nouveau produit'] écrasait silencieusement les vraies puces).
            const puces = (Array.isArray(currentProduct.features) ? currentProduct.features : [])
                .map((f) => String(f).trim())
                .filter(Boolean);

            const productData = {
                ...currentProduct,
                basePrice: parseFloat(base.toFixed(2)),
                price: parseFloat(finalPrice.toFixed(2)),
                originalPrice: finalOriginalPrice ? parseFloat(finalOriginalPrice.toFixed(2)) : null,
                badge: finalBadge,
                weight: parseFloat(currentProduct.weight || 0),
                stockQuantity: parseInt(currentProduct.stockQuantity),
                features: puces
            };

            if (modalMode === 'create') {
                await createProduct(productData);
                setSuccess('Produit créé avec succès');
                fetchProducts(); // Refresh full list for create
            } else {
                const updatedProduct = await updateProduct(currentProduct.id, productData);
                setSuccess('Produit mis à jour avec succès');

                // Optimistic update: update the product in the local state immediately
                setProducts(prevProducts =>
                    prevProducts.map(p => p.id === updatedProduct.id ? updatedProduct : p)
                );
            }

            setShowModal(false);
            setHasUnsavedChanges(false);
            setValidationErrors({});
            // fetchProducts(); // Removed global fetch to rely on local update for edits
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Helper to get preview price
    const getPreviewPrice = () => {
        const base = parseFloat(priceCalculation.basePriceInput);
        const percent = parseFloat(priceCalculation.discountPercent) || 0;
        if (!base) return '---';
        if (percent > 0) {
            return (base * (1 - percent / 100)).toFixed(2);
        }
        return base.toFixed(2);
    };

    if (loading && products.length === 0) {
        return (
            <div className="flex justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <h2 className="text-xl font-bold text-gray-900">Gestion des Stocks</h2>
                <button
                    onClick={openCreateModal}
                    className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                    + Nouveau Produit
                </button>
            </div>

            {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
                    {error}
                </div>
            )}
            {success && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-green-700 animate-fade-in">
                    {success}
                </div>
            )}

            <div className="overflow-hidden rounded-xl bg-white shadow-sm">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Produit</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Prix (€)</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Stock</th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {products.map(product => (
                                <tr key={product.id} className={`hover:bg-gray-50 ${product.stockQuantity <= 0 ? 'bg-red-50/50' : ''}`}>
                                    <td className="whitespace-nowrap px-6 py-4">
                                        <div className="flex items-center">
                                            <div className="h-10 w-10 flex-shrink-0">
                                                <img
                                                    className="h-10 w-10 rounded-full object-cover"
                                                    src={product.imageUrl || 'https://via.placeholder.com/40'}
                                                    alt=""
                                                    onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/40'; }}
                                                />
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                        <div className="flex flex-col">
                                            {product.originalPrice && (
                                                <span className="text-xs text-red-500 line-through">{Number(product.originalPrice).toFixed(2)}€</span>
                                            )}
                                            <span className="font-bold">{Number(product.price).toFixed(2)}€</span>
                                        </div>
                                    </td>
                                    <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                        <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${product.stockQuantity > 10 ? 'bg-green-100 text-green-800' :
                                            product.stockQuantity > 0 ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-red-100 text-red-800'
                                            }`}>
                                            {product.stockQuantity}
                                        </span>
                                    </td>
                                    <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                                        <button
                                            className="text-indigo-600 hover:text-indigo-900"
                                            onClick={() => openEditModal(product)}
                                            title="Modifier"
                                        >
                                            ✏️
                                        </button>
                                        <button
                                            className="ml-4 text-red-600 hover:text-red-900"
                                            onClick={async () => {
                                                if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ? Cette action est irréversible.')) {
                                                    try {
                                                        setLoading(true);
                                                        await deleteProduct(product.id);
                                                        setSuccess('Produit supprimé avec succès');
                                                        fetchProducts();
                                                        setTimeout(() => setSuccess(null), 3000);
                                                    } catch (err) {
                                                        setError(err.message);
                                                    } finally {
                                                        setLoading(false);
                                                    }
                                                }
                                            }}
                                            title="Supprimer"
                                        >
                                            🗑️
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Product Modal (Create & Edit) */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-black/50 p-4 backdrop-blur-sm">
                    <div className="relative w-full max-w-2xl rounded-xl bg-white shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between border-b p-4">
                            <h3 className="text-lg font-semibold text-gray-900">
                                {modalMode === 'create' ? 'Créer un nouveau produit' : 'Modifier le produit'}
                            </h3>
                            <button className="text-gray-400 hover:text-gray-500" onClick={handleCloseModal}>
                                <span className="sr-only">Fermer</span>
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Validation Errors */}
                        {Object.keys(validationErrors).length > 0 && (
                            <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                                <h4 className="text-sm font-bold text-red-800 mb-2">⚠️ Veuillez corriger les erreurs suivantes :</h4>
                                <ul className="text-sm text-red-700 list-disc list-inside">
                                    {Object.values(validationErrors).map((err, idx) => (
                                        <li key={idx}>{err}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <form onSubmit={handleModalSubmit} className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Nom du produit *</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={currentProduct.name}
                                        onChange={handleModalChange}
                                        required
                                        className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Tagline (Sous-titre)</label>
                                    <input
                                        type="text"
                                        name="tagline"
                                        value={currentProduct.tagline}
                                        onChange={handleModalChange}
                                        className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Description Courte (Accueil)</label>
                                <textarea
                                    name="description"
                                    value={currentProduct.description}
                                    onChange={handleModalChange}
                                    rows={2}
                                    className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Description Longue (Boutique)</label>
                                <textarea
                                    name="longDescription"
                                    value={currentProduct.longDescription}
                                    onChange={handleModalChange}
                                    rows={4}
                                    className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    placeholder="Description détaillée affichée dans la boutique..."
                                />
                            </div>

                            {/* Puces « ce que contient le pack ». Ce champ existait en base
                                mais n'était editable nulle part : seul un développeur
                                pouvait le remplir. C'est pourtant lui qui fait la
                                différence entre deux packs aux yeux d'un acheteur. */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Ce que contient le pack — une ligne par élément
                                </label>
                                <textarea
                                    value={(Array.isArray(currentProduct.features) ? currentProduct.features : []).join('\n')}
                                    onChange={(e) =>
                                        setCurrentProduct((p) => ({
                                            ...p,
                                            features: e.target.value.split('\n'),
                                        }))
                                    }
                                    rows={5}
                                    className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono"
                                    placeholder={'La boîte de jeu et ses 12 pièces\n2 pochons en coton pour les équipes\nLe questionnaire en carton 400 g'}
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Les lignes vides sont ignorées. C'est cette liste qui s'affiche sur la
                                    carte du pack, dans la boutique et sur la fiche produit.
                                </p>
                            </div>

                            <div className="bg-blue-50/50 p-3 rounded-lg border border-blue-100">
                                <h4 className="text-xs font-semibold text-blue-900 uppercase tracking-wider mb-2">Configuration Prix & Promo</h4>
                                <div className="grid grid-cols-3 gap-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Prix Base (€) *</label>
                                        <input
                                            type="number"
                                            name="basePriceInput"
                                            value={priceCalculation.basePriceInput}
                                            onChange={handleModalChange}
                                            step="0.01"
                                            required
                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm py-1.5"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Réduction (%)</label>
                                        <input
                                            type="number"
                                            name="discountPercent"
                                            value={priceCalculation.discountPercent}
                                            onChange={handleModalChange}
                                            step="1"
                                            min="0"
                                            max="100"
                                            placeholder="0"
                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm py-1.5"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">Prix Final</label>
                                        <div className="block w-full rounded-md border border-gray-200 bg-white px-3 py-1.5 text-gray-900 font-bold text-sm">
                                            {getPreviewPrice()} €
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Poids (kg)</label>
                                    <input
                                        type="number"
                                        name="weight"
                                        value={currentProduct.weight}
                                        onChange={handleModalChange}
                                        step="0.001"
                                        placeholder="0.5"
                                        className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Stock *</label>
                                    <input
                                        type="number"
                                        name="stockQuantity"
                                        value={currentProduct.stockQuantity}
                                        onChange={handleModalChange}
                                        required
                                        className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">URL Image *</label>
                                <input
                                    type="text"
                                    name="imageUrl"
                                    value={currentProduct.imageUrl}
                                    onChange={handleModalChange}
                                    placeholder="/images/product.jpg"
                                    className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Couleur (Thème)</label>
                                <select
                                    name="color"
                                    value={currentProduct.color}
                                    onChange={handleModalChange}
                                    className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                >
                                    <option value="pink">Rose (Flash Sale)</option>
                                    <option value="mint">Menthe (Premium)</option>
                                    <option value="sky">Ciel (Duo)</option>
                                    <option value="warm">Chaud (Famille)</option>
                                    <option value="purple">Violet (Mixte)</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Type de Variante</label>
                                <select
                                    name="variantType"
                                    value={currentProduct.variantType}
                                    onChange={handleModalChange}
                                    className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                >
                                    <option value="standard">Standard (Choix Fille/Garçon)</option>
                                    <option value="mixed">Mixte (Fille + Garçon)</option>
                                    <option value="boy">Garçon (Pré-sélectionné)</option>
                                    <option value="girl">Fille (Pré-sélectionnée)</option>
                                </select>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4 border-t">
                                <button
                                    type="button"
                                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                    onClick={handleCloseModal}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="inline-flex justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                >
                                    {modalMode === 'create' ? 'Créer le produit' : 'Enregistrer les modifications'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StockManager;
